import React from 'react';
import { handleChunkLoadError, isChunkLoadError } from '@/utils/chunkErrorHandling';

interface ChunkErrorBoundaryState {
  hasError: boolean;
}

export class ChunkErrorBoundary extends React.Component<
  React.PropsWithChildren,
  ChunkErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ChunkErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    if (isChunkLoadError(error, error.message)) {
      const reloaded = handleChunkLoadError();
      if (reloaded) {
        return;
      }
    }

    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-slate-900">
          <div className="max-w-xl space-y-4 text-center">
            <h1 className="text-2xl font-semibold">We hit a loading error</h1>
            <p className="text-base text-slate-700">
              We tried to reload the app to get the latest version, but the problem persisted.
              Please refresh the page or clear your browser cache and try again.
            </p>
            <button
              type="button"
              className="rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
